import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  Bell, AlertTriangle, Clock, Check, CheckCheck, X,
  ChevronRight, Shield, MessageSquare, Calendar
} from 'lucide-react';

const ALERT_TYPE_CONFIG = {
  overdue: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50', border: 'border-l-red-500', label: 'Overdue' },
  'deadline-approaching': { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-l-amber-500', label: 'Deadline Soon' },
  'compliance-due': { icon: Shield, color: 'text-orange-500', bg: 'bg-orange-50', border: 'border-l-orange-500', label: 'Compliance' },
};

const NOTIF_ICON_MAP = {
  task_deadline: Clock,
  task_overdue: AlertTriangle,
  leave_update: Calendar,
  whatsapp: MessageSquare,
};

const LoginAlertModal = ({ show, onClose }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    if (!show) return;
    const load = async () => {
      setLoading(true);
      try {
        const [notifs, alertData] = await Promise.allSettled([
          api.getNotifications(true, 50),
          api.getAlerts('active'),
        ]);
        if (notifs.status === 'fulfilled') setNotifications(notifs.value || []);
        if (alertData.status === 'fulfilled') setAlerts(alertData.value || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [show]);

  const handleMarkRead = async (notifId) => {
    try {
      await api.markNotificationRead(notifId);
      setNotifications(prev => prev.filter(n => n.notification_id !== notifId));
      toast.success('Marked as read');
    } catch { toast.error('Failed to mark as read'); }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await api.resolveAlert(alertId);
      setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      toast.success('Alert resolved');
    } catch { toast.error('Failed to resolve alert'); }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications([]);
      toast.success('All notifications marked as read');
    } catch { toast.error('Failed'); }
  };

  const handleNavigate = (path) => {
    onClose();
    navigate(path);
  };

  if (!show) return null;

  const totalCount = notifications.length + alerts.length;
  const overdueAlerts = alerts.filter(a => a.alert_type === 'overdue');
  const deadlineAlerts = alerts.filter(a => a.alert_type === 'deadline-approaching');
  const otherAlerts = alerts.filter(a => !['overdue', 'deadline-approaching'].includes(a.alert_type));

  const filteredNotifs = tab === 'alerts' ? [] : notifications;
  const filteredAlerts = tab === 'notifications' ? [] : alerts;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="login-alert-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden animate-in" data-testid="login-alert-content">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-900 to-slate-800 text-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-base font-bold">Alerts & Notifications</h2>
              <p className="text-xs text-slate-400">
                {loading ? 'Loading...' : `${totalCount} items require your attention`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="login-alert-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary Bar */}
        {!loading && totalCount > 0 && (
          <div className="flex items-center gap-2 px-5 py-2.5 bg-slate-50 border-b border-slate-100 flex-shrink-0 overflow-x-auto">
            <TabBtn active={tab === 'all'} onClick={() => setTab('all')} label="All" count={totalCount} />
            {notifications.length > 0 && (
              <TabBtn active={tab === 'notifications'} onClick={() => setTab('notifications')} label="Notifications" count={notifications.length} color="blue" />
            )}
            {alerts.length > 0 && (
              <TabBtn active={tab === 'alerts'} onClick={() => setTab('alerts')} label="Alerts" count={alerts.length} color="red" />
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto" data-testid="login-alert-list">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-sm text-slate-500">Loading alerts...</div>
            </div>
          ) : totalCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Check className="w-12 h-12 text-emerald-400 mb-3" />
              <p className="text-slate-700 font-medium">All clear!</p>
              <p className="text-sm text-slate-500 mt-1">No pending alerts or notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {/* Critical Alerts - Overdue */}
              {filteredAlerts.length > 0 && overdueAlerts.length > 0 && tab !== 'notifications' && (
                <AlertSection
                  title="Overdue Tasks"
                  items={overdueAlerts}
                  config={ALERT_TYPE_CONFIG.overdue}
                  onResolve={handleResolveAlert}
                  onNavigate={() => handleNavigate('/my-tasks')}
                />
              )}

              {/* Deadline Approaching Alerts */}
              {filteredAlerts.length > 0 && deadlineAlerts.length > 0 && tab !== 'notifications' && (
                <AlertSection
                  title="Deadlines Approaching"
                  items={deadlineAlerts}
                  config={ALERT_TYPE_CONFIG['deadline-approaching']}
                  onResolve={handleResolveAlert}
                  onNavigate={() => handleNavigate('/my-tasks')}
                />
              )}

              {/* Other Alerts */}
              {filteredAlerts.length > 0 && otherAlerts.length > 0 && tab !== 'notifications' && (
                <AlertSection
                  title="Other Alerts"
                  items={otherAlerts}
                  config={ALERT_TYPE_CONFIG['compliance-due']}
                  onResolve={handleResolveAlert}
                  onNavigate={() => handleNavigate('/alerts')}
                />
              )}

              {/* Notifications */}
              {filteredNotifs.length > 0 && (
                <div className="py-2">
                  <div className="flex items-center justify-between px-5 py-2">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Unread Notifications ({filteredNotifs.length})
                    </h3>
                    <button
                      onClick={handleMarkAllRead}
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      data-testid="login-alert-mark-all-read"
                    >
                      <CheckCheck className="w-3 h-3" /> Mark all read
                    </button>
                  </div>
                  {filteredNotifs.slice(0, 15).map(notif => (
                    <NotifRow key={notif.notification_id} notif={notif} onMarkRead={handleMarkRead} />
                  ))}
                  {filteredNotifs.length > 15 && (
                    <button
                      onClick={() => handleNavigate('/notifications')}
                      className="w-full text-center py-2 text-xs text-blue-600 hover:text-blue-700"
                    >
                      +{filteredNotifs.length - 15} more notifications
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50 flex-shrink-0">
          <button
            onClick={() => handleNavigate('/notifications')}
            className="text-xs text-slate-600 hover:text-blue-600 flex items-center gap-1"
            data-testid="login-alert-view-all"
          >
            View all <ChevronRight className="w-3 h-3" />
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
            data-testid="login-alert-dismiss"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

const TabBtn = ({ active, onClick, label, count, color = 'slate' }) => {
  const colors = {
    slate: active ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200',
    blue: active ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border border-blue-200',
    red: active ? 'bg-red-600 text-white' : 'bg-white text-red-600 border border-red-200',
  };
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex-shrink-0 ${colors[color]}`}
      data-testid={`login-alert-tab-${label.toLowerCase()}`}
    >
      {label}
      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${active ? 'bg-white/20' : 'bg-slate-100'}`}>
        {count}
      </span>
    </button>
  );
};

const AlertSection = ({ title, items, config, onResolve, onNavigate }) => {
  const Icon = config.icon;
  const showCount = 5;
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, showCount);

  return (
    <div className="py-2">
      <div className="flex items-center justify-between px-5 py-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${config.color}`} />
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title} ({items.length})</h3>
        </div>
        <button onClick={onNavigate} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
          View <ChevronRight className="w-3 h-3" />
        </button>
      </div>
      {visible.map(alert => (
        <div
          key={alert.alert_id}
          className={`flex items-center gap-3 px-5 py-2.5 border-l-4 ${config.border} hover:bg-slate-50 transition-colors`}
          data-testid={`login-alert-item-${alert.alert_id}`}
        >
          <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-4 h-4 ${config.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{alert.task_name || alert.message}</p>
            <p className="text-xs text-slate-500 truncate">{alert.message}</p>
          </div>
          <button
            onClick={() => onResolve(alert.alert_id)}
            className="p-1.5 hover:bg-emerald-100 rounded-lg transition-colors flex-shrink-0"
            title="Mark as resolved"
            data-testid={`resolve-alert-${alert.alert_id}`}
          >
            <Check className="w-4 h-4 text-emerald-600" />
          </button>
        </div>
      ))}
      {items.length > showCount && !expanded && (
        <button onClick={() => setExpanded(true)} className="w-full text-center py-2 text-xs text-blue-600 hover:text-blue-700">
          +{items.length - showCount} more {title.toLowerCase()}
        </button>
      )}
    </div>
  );
};

const NotifRow = ({ notif, onMarkRead }) => {
  const IconComp = NOTIF_ICON_MAP[notif.notification_type] || Bell;
  const priorityBorder = notif.priority === 'urgent' ? 'border-l-red-500' : notif.priority === 'high' ? 'border-l-orange-500' : 'border-l-blue-300';

  return (
    <div
      className={`flex items-center gap-3 px-5 py-2.5 border-l-4 ${priorityBorder} hover:bg-slate-50 transition-colors`}
      data-testid={`login-notif-${notif.notification_id}`}
    >
      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
        <IconComp className="w-4 h-4 text-blue-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{notif.title}</p>
        <p className="text-xs text-slate-500 truncate">{notif.message}</p>
      </div>
      <button
        onClick={() => onMarkRead(notif.notification_id)}
        className="p-1.5 hover:bg-emerald-100 rounded-lg transition-colors flex-shrink-0"
        title="Mark as read"
        data-testid={`mark-read-${notif.notification_id}`}
      >
        <Check className="w-4 h-4 text-emerald-600" />
      </button>
    </div>
  );
};

export default LoginAlertModal;
